"use client";

import { forwardRef } from "react";
import styled from "styled-components";
import { jsx as _jsx } from "react/jsx-runtime";
const Img = styled.img.withConfig({
  componentId: "sc-voqve2-0"
})(["max-width:100%;", ";"], ({
  focalPoint
}) => focalPoint ? `height: 100%;
         width: 100%;
         object-fit: cover;
         object-position: ${focalPoint.x} ${focalPoint.y};
      ` : "");

/**
 * A wrapper for an `img` element that defaults to lazy load images and adding styling needed for the `focalPoint` option when provided.
 * @param {object} props - The component props
 * @param {string} [props.src] - The path to an image
 * @param {string} [props.srcSet] - A list of one or more strings separated by commas indicating a set of possible image sources
 * @param {string} [props.sizes] - A list of one or more strings separated by commas indicating a set of source sizes
 * @param {string} [props.alt] - Textual description of the image as a fallback if the image can't load and for accessibility
 * @param {boolean} [props.disableLazy] - Whether to disable the default lazy loading
 * @param {{ x: string, y: string }} [props.focalPoint] - x and y coordinates that denote the focus point of the image
 * @param {Function} [props.onLoad] - A callback invoked when the image has finished loading. Callback function will receive an Event object whose target is the native image element.
 * @param {React.Ref} ref - Ref forwarded to the underlying element.
 * @returns {React.ReactNode} - The Image component
 */
const ImageComponent = ({
  src,
  srcSet,
  sizes,
  alt,
  disableLazy,
  focalPoint,
  onLoad,
  ...props
}, ref) => /*#__PURE__*/_jsx(Img, {
  ref: ref,
  src: src,
  srcSet: srcSet,
  sizes: sizes,
  alt: alt,
  role: alt ? null : "presentation",
  focalPoint: focalPoint,
  onLoad: onLoad,
  loading: disableLazy ? "eager" : "lazy",
  ...props
});
const Image = /*#__PURE__*/forwardRef(ImageComponent);
Image.displayName = "Image";
export default Image;